import { useCallback, useEffect, useState } from "react";

import { dayMonthMaybeYear } from "@/lib/dates";
import { useDialogs } from "@/components/shell/Dialogs";
import {
  saveStatsSettings,
  statsPredicted,
  statsRecall,
  statsTrend,
  type Distribution,
  type RetentionCell,
  type StatsPredicted,
  type StatsRecall,
  type StatsTrend,
} from "@/lib/stats";

import { CALIBRATION_PANEL, Calibration } from "./Calibration";
import { Band, Columns, useWideLayout } from "./layout";
import { Panel, RowLabel, StatList, StatValue, Th } from "./Panel";
import { ComputingNote, SkeletonChart, SkeletonRows, StatSkeleton } from "./Skeleton";
import { Bars } from "./chart/Bars";
import { useChartHover, type HoverContent } from "./chart/HoverCard";
import { Line, LineLegend } from "./chart/Line";
import { SegmentLegend, StackedBar } from "./chart/StackedBar";
import { clamp } from "./chart/scale";

const pct = (value: number | null, digits = 1) =>
  value === null ? "—" : `${(value * 100).toFixed(digits)}%`;

/**
 * Eleven headings, in one place — because `RecallSkeleton` draws all eleven cards in their designed
 * places while `/recall` is in flight, so every title and note below was written **twice**.
 *
 * That is a third of this file's copy existing as a second copy, and the two were already free to
 * disagree: the loaded `Retention trend` note said `rolling 30 days, full history` from the
 * server's own window while the skeleton hardcoded 30, so a server that changed the window would
 * have had the skeleton and the panel print different numbers for the same chart, a tenth of a
 * second apart. `trendNote` is that fixed — a function of the window, with `TREND_WINDOW` as the
 * skeleton's admitted guess.
 *
 * **未習熟 / 習熟 rather than the canvas's 若い / 熟成** (Alex). Those are Anki's own Japanese for
 * the two interval bands and `cards/query.ts`'s `STATE_LABEL` already uses them, so one concept
 * gets one word across the app — see `docs/CHROME.md`.
 *
 * **忘却 rather than the canvas's 失敗** for lapses: 失敗 already means a failed *job* in Setup,
 * and a word that means two things in one product is worse than a word that agrees with no canvas.
 *
 * 実定着率 / 定着率 and 正答率 stay distinct, which is the canvas's own choice: true retention is
 * one review per card that came due, a pass rate counts every attempt, and this tab draws both.
 */
const PANELS = {
  trueRetention: {
    title: "実定着率",
    check: "true-retention",
    note: "復習のみ · 各率の下に 合格 / 不可",
  },
  todaySoFar: { title: "今日ここまで", check: "today-so-far" },
  predictedRecall: { title: "予測再生率", check: "predicted-recall" },
  trend: { title: "定着率の推移", check: "retention-trend" },
  difficulty: { title: "難易度", check: "difficulty" },
  passRate: {
    title: "難易度別の正答率",
    check: "pass-rate-by-difficulty",
    note: "復習のみ · 全履歴",
  },
  stability: { title: "安定度", check: "stability" },
  answerButtons: { title: "解答ボタン", check: "answer-buttons", note: "12か月" },
  reviewsByHour: { title: "時間帯別の復習", check: "reviews-by-hour", note: "12か月 · 棒は復習数" },
  lapses: { title: "忘却", check: "lapses" },
} as const;

/** `直近30日・全履歴`, from the window the SERVER used rather than from a constant here. */
const trendNote = (windowDays: number) => `直近${windowDays}日・全履歴`;
/** What the skeleton has to guess with, having no payload yet. The server's default. */
const TREND_WINDOW = 30;

/** Units and captions a `StatValue` and its `StatSkeleton` both have to spell. */
const UNITS = {
  todayReviewed: "枚を復習",
  predictedToday: "今日期限の平均再生確率",
  predictedWeek: "今後7日以内が期限",
} as const;

/**
 * Recall — `8b`. How hard are my cards, and is the scheduler right.
 *
 * The reading order is deliberate and is the panel order: the **table** states where recall
 * actually is, **today** sits beside its baseline with its denominator, and the distributions are
 * supporting evidence rather than five equal cards on a dashboard.
 *
 * **Two designed arrangements, chosen in JS** — the frames' 1147 and 1920 layouts partition these
 * panels differently rather than being one layout at two sizes. See `layout.tsx`.
 */
export function Recall({ deck, onOpenJob }: { deck: string; onOpenJob?: () => void }) {
  const [data, setData] = useState<StatsRecall | null>(null);
  const [error, setError] = useState<string | null>(null);
  const dialogs = useDialogs();
  // **One hover state for the whole tab**, not one per panel. The card is portalled to the body and
  // positioned against the viewport, so where the hook lives says nothing about where it draws —
  // and a pointer can only be over one mark, so a second state could only ever be a second card.
  const { onEnter, onLeave, card } = useChartHover();
  // Which of the two designed arrangements this window gets. Resolved in JS because the frames'
  // 1147 and 1920 layouts group these panels differently — see `layout.tsx`.
  const wide = useWideLayout();
  // **One `/trend` request for the tab, not one per panel.** Two panels read it — the retention
  // trend and pass-rate-by-difficulty — and each fetching for itself doubled a route that is two
  // revlog scans, ~4s of batch-handle work per visit, for a response they both wanted whole.
  // `undefined` is in flight, `null` is failed; both panels distinguish them.
  const [trend, setTrend] = useState<StatsTrend | null | undefined>(undefined);
  // **Its own request, on the same terms as `/trend`.** Two `card_stats_data` loops — 3.2 s,
  // measured — for two numbers on one panel. `undefined` is in flight, `null` is failed.
  const [predicted, setPredicted] = useState<StatsPredicted | null | undefined>(undefined);

  useEffect(() => {
    let live = true;
    setPredicted(undefined);
    void statsPredicted(deck)
      .then((found) => live && setPredicted(found))
      .catch(() => live && setPredicted(null));
    return () => {
      live = false;
    };
  }, [deck]);

  useEffect(() => {
    let live = true;
    setTrend(undefined);
    void statsTrend(deck)
      .then((found) => live && setTrend(found))
      .catch(() => live && setTrend(null));
    return () => {
      live = false;
    };
  }, [deck]);

  const load = useCallback(() => {
    let live = true;
    void statsRecall(deck)
      .then((found) => live && setData(found))
      .catch((e) => live && setError(e instanceof Error ? e.message : String(e)));
    return () => {
      live = false;
    };
  }, [deck]);

  useEffect(() => {
    setData(null);
    setError(null);
    return load();
  }, [load]);

  /**
   * The line retention is judged against.
   *
   * **Editable, because otherwise it is an invisible constant.** It defaults to 90% and Alex's deck
   * is scheduling at 85 — so the trend's dashed rule and calibration's `Actual at X% predicted` were
   * both answering a question nobody had asked, with no way to see or change the number (he asked
   * where it was, 8 Aug 2026). Deliberately still *not* read from the deck: it is the standard he
   * holds the scheduler to, and the panel now prints both when they differ.
   */
  const editTarget = useCallback(async () => {
    if (!data) return;
    const answer = await dialogs.prompt({
      title: "目標定着率",
      label: "目標",
      hint: "推移と較正を判定する基準の割合。デッキ自身の FSRS 設定は別で、値が違うときは両方表示されます。",
      initial: String(Math.round(data.target * 100)),
      submitLabel: "保存",
    });
    if (answer === null) return;
    const percent = Number(answer);
    if (!Number.isFinite(percent) || percent <= 0 || percent > 100) return;
    try {
      await saveStatsSettings({ fsrsTarget: percent / 100 });
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [data, dialogs, load]);

  if (error) return <p className="p-[26px] text-secondary text-accent-text">{error}</p>;
  // **The tab's own shape while `/recall` is in flight**, rather than the word `Loading…` where the
  // screen should be. Cold — the first view after a review syncs down — that read is ~1.3 s; warm
  // it is ~0.1 s and this is a frame or two. Both are better served by the panels being there.
  if (!data) return <RecallSkeleton wide={wide} />;

  /**
   * Every panel is built once here, then PLACED by one of the two arrangements below.
   *
   * The frames draw this tab at 1147 and at 1920, and the two are different *partitions*
   * of these eleven panels rather than one layout at two sizes — `Lapses` is bottom-right
   * in one and in the top band in the other, `Stability` moves out of a pair and into the
   * middle of a five-across strip. Naming the panels is what makes writing both
   * affordable; the alternative is the same JSX twice, and two copies of a panel drift.
   */
  const trueRetention = (
    <Panel {...PANELS.trueRetention}>
      <table className="w-full">
        <thead>
          <tr>
            <Th align="left" width={96} />
            <Th>未習熟</Th>
            <Th>習熟</Th>
            <Th>すべて</Th>
          </tr>
        </thead>
        <tbody>
          {data.retention.map((row) => (
            <tr key={row.period} className="border-t border-line-soft">
              <RowLabel>{row.label}</RowLabel>
              <Cell cell={row.young} />
              <Cell cell={row.mature} />
              <Cell cell={row.all} />
            </tr>
          ))}
        </tbody>
      </table>
      {/* Stated on screen, not left in a comment: what a number excludes is part of what it
          means, and these exclusions are Anki's rather than ours. */}
      <p className="text-[14px]/[1.5] text-ink-4">
        除外: 学習ステップ、再学習ステップ、フィルタデッキ、手動の再スケジュール。未習熟は復習時点で21日未満。
      </p>
    </Panel>
  );

  /* **`cards reviewed`, not `reviews`**, and the wording is what makes 190 make sense on a
      day the load axis calls 284 (Alex, 8 Aug 2026). The difference is learning and
      relearning steps: a failed card comes back the same day and every attempt is a review,
      but only one of them is a *review* in Anki's true-retention sense — so the figure is
      one per card that came due, which is what the label now says.

      Strictly this is Anki's review-type review count, and it equals the distinct card
      count except under filtered-deck rescheduling, which would give a card two review-type
      answers in one day. This collection has none, and the label is the honest reading of
      the number for every collection that does not. */
  const todaySoFar = (
    <Panel {...PANELS.todaySoFar} gap={10}>
      <StatValue
        value={pct(data.today.rate)}
        unit={`${data.today.reviews.toLocaleString()}${UNITS.todayReviewed}`}
        tone={data.today.thin ? "var(--ink-3)" : "var(--ink)"}
      />
      {/* Rule 1 — no rate appears alone. Below the small-sample threshold the comparison is
          de-emphasised rather than removed: it is still the right baseline, it just is not
          yet something to conclude from. */}
      <div style={data.today.thin ? { opacity: 0.55 } : undefined}>
        <StatList
          rows={[
            // The server's own period words (`30日`, `12か月`), suffixed rather than prefixed:
            // Japanese marks the comparison after the thing compared, so `直近30日比` where the
            // English put `vs` in front.
            ...data.today.baselines.map(
              ([label, rate]) => [`${label}比`, pct(rate)] as [string, string],
            ),
            ["今日の復習枚数", data.today.reviews.toLocaleString()],
          ]}
        />
      </div>
      {data.today.thin && (
        <p className="text-[14px]/[1.5] text-ink-4">
          傾向として読むにはまだ復習が少なすぎます。
        </p>
      )}
    </Panel>
  );

  const predictedRecall = (
    <Panel
      {...PANELS.predictedRecall}
      note={data.fsrs ? "FSRS" : "FSRS はこのデッキを管理していません"}
      gap={10}
      right={
        <button
          onClick={editTarget}
          className="rounded-[var(--r-2)] border border-line px-[9px] py-[3px] text-[14px]/[1.4] text-ink-2 hover:border-line-strong"
        >
          目標: {pct(data.target, 0)} · 変更
        </button>
      }

    >
      {data.fsrs ? (
        <>
          {/* **The two slow figures, and the only part of this panel that waits.** Everything else
              here — the heading, the target button, the deck-wide mean and the preset's own
              retention — comes off `/recall` and is on screen while these compute. */}
          <div className="flex flex-wrap gap-[26px]">
            {predicted === undefined ? (
              <>
                <StatSkeleton note={UNITS.predictedToday} />
                <StatSkeleton note={UNITS.predictedWeek} />
              </>
            ) : (
              <>
                <StatValue
                  value={pct(predicted?.dueToday ?? null)}
                  size={26}
                  note={UNITS.predictedToday}
                />
                <StatValue
                  value={pct(predicted?.dueWeek ?? null)}
                  size={26}
                  note={UNITS.predictedWeek}
                />
              </>
            )}
          </div>
          <StatList
            rows={[
              [
                "予測 − 実測（今日）",
                predicted === undefined
                  ? "…"
                  : predicted?.dueToday == null || data.today.rate === null
                    ? "—"
                    : `${((predicted.dueToday - data.today.rate) * 100).toFixed(1)}pt`,
              ],
              ["デッキ全体の平均再生確率", pct(data.retrievability.average)],
              // **What FSRS is scheduling towards** — the deck preset's own setting, which
              // is a different question from the line this app judges against. That second
              // one is not a row: it is the button in this panel's corner, which both states
              // it and changes it (Alex, 8 Aug 2026 — a row saying the same thing two inches
              // away is the kind of duplication that ages badly).
              ...(data.desiredRetention === null
                ? []
                : ([["FSRS の目標定着率", pct(data.desiredRetention, 0)]] as [
                    string,
                    string,
                  ][])),
            ]}
          />
        </>
      ) : (
        <p className="text-[14px]/[1.5] text-ink-3">
          {/* **Two different problems, and one used to read as the other.** A deck whose
              preset carries FSRS parameters while the collection switch is off is what a
              restored collection looks like — the preset travels in an `.apkg` and the switch
              does not — and "turn it on in the deck options" sends you to a screen where it
              already is on. Cost an hour on 8 Aug 2026 on staging. */}
          {data.fsrsConfigured ? (
            <>
              このデッキのプリセットには FSRS パラメータ
              {data.desiredRetention !== null &&
                `と目標定着率 ${pct(data.desiredRetention, 0)}`}
              がありますが、<strong>コレクション</strong>側で FSRS
              が無効になっているため、Anki
              は難易度・安定度・予測再生率をどれも返しません。復元したコレクションでは、これは設定を変えたからではなく復元によるものです。{" "}
              {/* **Naming the fix, not just the cause.** The first version of this only
                  explained, which is the wrong half: the reader is looking at a panel that
                  should have numbers in it and wants them back. Anki desktop is the fix on a
                  machine that has it; on a hub nobody syncs to — staging — it is the script,
                  because collection config only moves by a full round trip. The command is a
                  path and stays exactly as it is typed. */}
              Anki で戻すか（デッキオプション → FSRS）、クライアントのつながっていないハブでは{" "}
              <span className="font-mono text-[14px]">
                scripts/set_collection_flags.py --fsrs on
              </span>
              を実行してください。
            </>
          ) : (
            <>
              難易度・安定度・予測再生率は FSRS
              から得られます。デッキオプションで有効にすると表示されます。
            </>
          )}
        </p>
      )}
    </Panel>
  );

  const trendPanel = (
    <TrendPanel trend={trend} target={data.target} fill={wide} />
  );

  const calibration = (
    <Calibration deck={deck} onOpenJob={onOpenJob} />
  );

  const difficulty = data.fsrs ? (
    <Panel
      {...PANELS.difficulty}
      note={`中央値${data.difficulty.median}% · 平均${pct(data.difficulty.average, 0)}`}
      gap={6}
    >
      <BucketBars
        dist={data.difficulty}
        unit="難易度"
        fill={wide}
        // The design's own emphasis: the hard band is the one worth spotting from
        // across the screen, and on this collection it holds half the deck.
        color={(low) => (low >= 90 ? "var(--accent-text)" : "var(--trend-mature)")}
        onEnter={onEnter}
        onLeave={onLeave}
      />
      <span className="text-[14px]/[1.4] text-ink-4">
        90超が{(data.difficulty.shareHigh * 100).toFixed(0)}% · 中央値{data.difficulty.median}
      </span>
    </Panel>
  ) : null;

  const passRate = (
    <DifficultyBandsPanel trend={trend} target={data.target} />
  );

  const stability = data.fsrs ? (
    <Panel
      {...PANELS.stability}
      note={`中央値${data.stability.median.toLocaleString()}日 · カードが保つ長さ`}
      gap={6}
    >
      <BucketBars
        dist={data.stability}
        unit="安定度"
        fill={wide}
        color={() => "var(--trend-young)"}
        onEnter={onEnter}
        onLeave={onLeave}
      />
    </Panel>
  ) : null;

  const answerButtons = (
    <Panel
      {...PANELS.answerButtons}
      gap={9}
      // **Four segments, four values.** `again`/`hard` were both `--accent-text` and `good`/`easy`
      // both `--ink-3`, which is the failure `tokens.css` states in full beside `--cover-learned`:
      // *"a stacked bar whose first two segments are the same colour has three segments"*. Here it
      // makes two, and the legend then names four things the chart cannot tell apart. Each pair is
      // one step apart within its own family — red for the answers that cost you, ink for the ones
      // that did not — so the one-accent rule is kept and the bar is readable.
      // **Anki's own four Japanese grade words**, not a translation of the English ones. This is
      // the same collection Anki desktop grades, so the buttons it counts have names the reader
      // already sees on the other client. Review's two buttons are 不可 / 合格 and are deliberately
      // NOT these: that surface removed Hard and Easy, so it names a verdict rather than a button.
      right={
        <SegmentLegend
          items={[
            { label: "不可", color: "var(--accent-text)" },
            { label: "難しい", color: "var(--accent-deep)" },
            { label: "合格", color: "var(--ink-3)" },
            { label: "簡単", color: "var(--ink-4)" },
          ]}
        />
      }
    >
      {data.buttons.map((row) => (
        <div key={row.label} className="flex flex-col gap-[4px]">
          <div className="flex items-baseline gap-[8px]">
            <span className="flex-1 text-[14px]/[19.6px] font-semibold text-ink">
              {row.label}
            </span>
            <span className="font-mono text-[14px]/[1.4] text-ink-3">
              {row.total.toLocaleString()}回
            </span>
            <span className="w-[52px] text-right font-mono text-[14px]/[19.6px] font-semibold text-ink">
              {pct(row.rate)}
            </span>
          </div>
          <StackedBar
            height={11}
            segments={(
              [
                ["不可", row.again, "var(--accent-text)"],
                ["難しい", row.hard, "var(--accent-deep)"],
                ["合格", row.good, "var(--ink-3)"],
                ["簡単", row.easy, "var(--ink-4)"],
              ] as const
            ).map(([label, value, color]) => ({
              value,
              color,
              hover: {
                title: `${row.label} · ${label}`,
                rows: [
                  { label: "復習数", value: value.toLocaleString() },
                  {
                    label: "この行に占める割合",
                    value: row.total > 0 ? `${((100 * value) / row.total).toFixed(1)}%` : "—",
                  },
                ],
              },
            }))}
            onEnter={onEnter}
            onLeave={onLeave}
          />
        </div>
      ))}
    </Panel>
  );

  const reviewsByHour = (
    <Panel
      {...PANELS.reviewsByHour}
      gap={6}
      right={
        <span className="flex items-center gap-[5px] text-[14px]/[1.4] text-ink-2">
          <span
            aria-hidden
            className="h-[2px] w-[12px]"
            style={{ background: "var(--hour-rate)" }}
          />
          正答率 70〜90%
        </span>
      }
    >
      <Bars
        height={78}
        fill={wide}
        // The design's pass-rate marker: a rule per bar over a **chosen** 70–90% band, which
        // is where a real day's hourly rates live. Fitted to the data it would turn a flat
        // day into a mountain range, which is `Line`'s argument at a smaller scale.
        overlay={{
          values: data.hours.map((row) => row.rate),
          domain: [0.7, 0.9],
          color: "var(--hour-rate)",
        }}
        bars={data.hours.map((row) => ({
          value: row.reviews,
          color: "var(--load-future)",
          hover: {
            title: `${String(row.hour).padStart(2, "0")}:00`,
            rows: [
              { label: "復習数", value: row.reviews.toLocaleString() },
              { label: "正答率", value: pct(row.rate) },
            ],
          },
        }))}
        gap={2}
        onEnter={onEnter}
        onLeave={onLeave}
      />
      <div className="flex justify-between text-[14px]/[1.4] text-ink-4">
        {[0, 6, 12, 18, 23].map((hour) => (
          <span key={hour} className="font-mono">
            {String(hour).padStart(2, "0")}
          </span>
        ))}
      </div>
    </Panel>
  );

  const lapses = (
    <Panel key="lapses" {...PANELS.lapses} gap={10}>
      <StatList
        rows={data.lapses.map((row) => [
          row.label,
          `${row.count.toLocaleString()} · ${pct(row.rate)}`,
        ])}
      />
      <div className="flex items-baseline justify-between gap-[12px] border-t border-line-soft pt-[10px]">
        {/* `leech` stays: it is an Anki tag that exists on these notes, not a word for it. */}
        <span className="text-[14px]/[19.6px] text-ink-2">leech タグのカード</span>
        <span className="text-[20px]/[1.2] font-semibold" style={{ color: "var(--accent-text)" }}>
          {data.leeches.toLocaleString()}
        </span>
      </div>
    </Panel>
  );

  /**
   * `1920` — the frames' three bands, with their own pixel widths as the flex ratios.
   *
   * This is a real re-grouping rather than the narrow layout stretched: `Lapses` joins the top band
   * beside the two summary cards, and the five distributions become one strip across the bottom.
   */
  if (wide) {
    return (
      <div className="flex flex-col gap-[14px]">
        <Band
          items={[
            [759, trueRetention],
            [336, todaySoFar],
            [366, predictedRecall],
            [306, lapses],
          ]}
        />
        <Band items={[[1024, trendPanel], [769, calibration]]} />
        <Band
          items={[
            [298, difficulty],
            [325, passRate],
            [377, stability],
            [430, reviewsByHour],
            [325, answerButtons],
          ]}
        />
        {card}
      </div>
    );
  }

  /**
   * `1147` and below — a wide column beside a 352px one, each flowing on its own.
   *
   * The pairings are the frames': calibration next to the difficulty pair (is the scheduler right,
   * and about what), stability next to the answer buttons (how long cards hold, and how they are
   * answered), the hourly volume next to lapses. What is NOT the frames' is that these are columns
   * rather than rows — see `Columns`.
   *
   * The trend spans the width in the middle because it is the only genuine time series here, which
   * is also why it is not in either column.
   */
  return (
    <div className="flex flex-col gap-[14px]">
      <Columns wide={[trueRetention]} narrow={[todaySoFar, predictedRecall]} />
      {trendPanel}
      <Columns
        wide={[calibration, stability, reviewsByHour]}
        narrow={[difficulty, passRate, answerButtons, lapses]}
      />
      {card}
    </div>
  );
}


/**
 * The rolling retention trend — the only thing on any of these screens that answers *is this
 * getting better or worse*. Anki has no equivalent; every retention view it offers is a fixed
 * bucket.
 *
 * **Its own request**, because it is the only read in Stats that runs on the batch handle — a
 * revlog scan rather than the one `graphs()` call the rest of the tab shares. Folding it into
 * `/recall` would make the whole tab wait on the slow half.
 */
function TrendPanel({
  trend,
  target,
  fill,
}: {
  trend: StatsTrend | null | undefined;
  target: number;
  /** Grow with the panel — see `Bars`. In band 2 this card is as tall as `Calibration` beside it. */
  fill?: boolean;
}) {
  const { onEnter, onLeave, card } = useChartHover();

  if (trend === null) return null;
  if (!trend) {
    // **The panel's own shape, not a sentence.** This is a whole-revlog scan on the batch handle —
    // 724 ms over 576k rows — so it is genuinely the last thing on the tab to arrive, and it draws
    // a band at the chart's own height with its axis labels already in place. The `right` slot says
    // what is being computed, which is a fact about this panel rather than about the screen.
    return (
      <Panel
        {...PANELS.trend}
        note={trendNote(TREND_WINDOW)}
        gap={8}
        right={<ComputingNote>復習履歴を読み込み中</ComputingNote>}
      >
        <SkeletonChart height={fill ? 240 : 130} />
      </Panel>
    );
  }
  if (trend.points.length === 0) {
    return (
      <Panel {...PANELS.trend} note={trendNote(trend.windowDays)}>
        <p className="text-[14px]/[1.5] text-ink-3">
          履歴がまだ足りません — 直近{trend.windowDays}日の率には、その日数ぶんの復習が必要です。
        </p>
      </Panel>
    );
  }

  const latest = trend.points[trend.points.length - 1];
  const first = trend.points[0];
  return (
    <Panel
      {...PANELS.trend}
      note={trendNote(trend.windowDays)}
      gap={8}
      right={
        <LineLegend
          items={[
            { label: "未習熟の正答率", color: "var(--trend-young)", value: pct(latest?.young ?? null) },
            { label: "習熟の正答率", color: "var(--trend-mature)", value: pct(latest?.mature ?? null) },
            { label: `目標${pct(target, 0)}`, color: "var(--trend-target)" },
          ]}
        />
      }
    >
      <Line
        height={130}
        fill={fill}
        // 60-100% is CHOSEN, not fitted. An auto-fitted axis rescales every time the data moves,
        // so a flat year renders as a crisis and a real collapse as a gentle slope.
        domain={[60, 100]}
        target={target * 100}
        series={[
          {
            // These two are the SVG's accessible name, joined by `Line` — the chart has no other
            // label, so a screen reader gets `未習熟と習熟` for it.
            label: "未習熟",
            color: "var(--trend-young)",
            values: trend.points.map((p) => (p.young === null ? null : p.young * 100)),
          },
          {
            label: "習熟",
            color: "var(--trend-mature)",
            values: trend.points.map((p) => (p.mature === null ? null : p.mature * 100)),
          },
        ]}
        onEnter={onEnter}
        onLeave={onLeave}
        hoverAt={(index) => {
          const point = trend.points[index];
          if (!point) return null;
          // A window with too few reviews has no rate at all, and the line is a **gap** there. The
          // card says so rather than reporting `—` three times, which would read as a rendering
          // failure on a chart that is deliberately broken at that point.
          if (point.young === null && point.mature === null) {
            return {
              title: dayMonthMaybeYear(new Date(`${point.day}T00:00:00`)),
              rows: [{ label: "期間", value: "率を出すには復習が少なすぎます" }],
            };
          }
          return {
            title: dayMonthMaybeYear(new Date(`${point.day}T00:00:00`)),
            rows: [
              { label: "未習熟", value: pct(point.young) },
              { label: "習熟", value: pct(point.mature) },
              { label: "すべて", value: pct(point.all) },
              { label: "期間", value: `ここまでの${trend.windowDays}日` },
            ],
          };
        }}
      />
      {card}
      <div className="flex justify-between text-[14px]/[1.4] text-ink-4">
        <span>{first && dayMonthMaybeYear(new Date(`${first.day}T00:00:00`))}</span>
        <span>{latest && dayMonthMaybeYear(new Date(`${latest.day}T00:00:00`))}</span>
      </div>
    </Panel>
  );
}

function Cell({ cell }: { cell: RetentionCell }) {
  return (
    <td className="px-[6px] py-[7px] text-right">
      <div
        className="font-mono text-[14px]/[19.6px] font-semibold text-ink"
        // Thin samples are greyed rather than hidden: the number is real, it is just not yet
        // something to conclude from.
        style={cell.thin ? { color: "var(--ink-3)" } : undefined}
      >
        {pct(cell.rate)}
      </div>
      {/* The counts under every rate. This is what makes it a table rather than a chart, and what
          lets a 100% day be recognised as four reviews. */}
      <div className="font-mono text-[14px]/[1.5] text-ink-4">
        {cell.passed.toLocaleString()} / {cell.failed.toLocaleString()}
      </div>
    </td>
  );
}


/**
 * A binned distribution as bars, with the bin labels beneath.
 *
 * **Binned on the server**, not here: the thresholds are Anki's own units and the panel must not be
 * a second place that decides what a band means. What this adds is the axis — a bar row with no
 * labels is a shape rather than a chart, which is what stability was with 927 unlabelled bars.
 *
 * Only every other label is drawn past a handful of bins. Twenty labels under twenty bars at 11px
 * is a grey line; the hover card carries the exact band for every one of them.
 */
function BucketBars({
  dist,
  unit,
  color,
  fill,
  onEnter,
  onLeave,
}: {
  dist: Distribution;
  unit: string;
  color: (low: number) => string;
  /** Grow with the panel — see `Bars`. The label strip beneath keeps its natural height. */
  fill?: boolean;
  onEnter: (event: { currentTarget: Element }, content: HoverContent) => void;
  onLeave: () => void;
}) {
  const total = dist.buckets.reduce((sum, b) => sum + b.count, 0) || 1;
  // **Three labels for a long distribution, all of them for a short one.** Twenty labels under
  // twenty bars in a 300px panel truncate to `7…`, `1…`, `4…`, which is worse than none; the design
  // draws exactly three under its stability chart for the same reason. The hover card carries the
  // exact band for every bar either way.
  const sparse = dist.buckets.length > 8;
  const marks = sparse
    ? [0, Math.floor((dist.buckets.length - 1) / 2), dist.buckets.length - 1]
    : dist.buckets.map((_, index) => index);
  return (
    <>
      <Bars
        height={70}
        fill={fill}
        bars={dist.buckets.map((bucket) => ({
          value: bucket.count,
          color: color(bucket.low),
          hover: {
            // `${band} の${unit}` — the bin label is a bare range (`50s`, `1–3d`) and the unit
            // says which quantity it is a range OF, which is the same job the English trailing
            // noun did.
            title: `${bucket.label} の${unit}`,
            rows: [
              { label: "枚数", value: bucket.count.toLocaleString() },
              { label: "割合", value: `${((100 * bucket.count) / total).toFixed(1)}%` },
            ],
          },
        }))}
        onEnter={onEnter}
        onLeave={onLeave}
      />
      <div
        className={`flex text-[14px]/[1.2] text-ink-4 ${sparse ? "justify-between" : ""}`}
      >
        {sparse
          ? marks.map((index) => (
              <span key={index} className="whitespace-nowrap">
                {dist.buckets[index]?.label}
              </span>
            ))
          : dist.buckets.map((bucket) => (
              <span key={bucket.label} className="min-w-0 flex-1 truncate text-center">
                {bucket.label}
              </span>
            ))}
      </div>
    </>
  );
}

/**
 * Pass rate by the card's difficulty — the design's panel beside the difficulty histogram.
 *
 * The pair is the argument: one says how many cards are hard, this says whether *hard* means what
 * FSRS says it means. A monotone fall from the easy band to the hard one is the estimate working; a
 * flat line means it is not discriminating at all, which no other panel on the tab would reveal.
 *
 * **Its own request, sharing the trend's**, because it is the same kind of read — a revlog scan on
 * the batch handle — and the alternative is making the whole fast tab wait for it.
 */
function DifficultyBandsPanel({
  trend,
  target,
}: {
  trend: StatsTrend | null | undefined;
  target: number;
}) {
  const { onEnter, onLeave, card } = useChartHover();

  const bands = trend?.difficultyBands.filter((band) => band.reviews > 0) ?? [];
  const measured = bands.filter((band) => band.rate !== null).map((band) => band.rate as number);
  const spread = measured.length > 1 ? Math.max(...measured) - Math.min(...measured) : null;

  return (
    <Panel {...PANELS.passRate} gap={7}>
      {!trend ? (
        // Four rows, because the panel draws four bands. See `Skeleton.tsx` on why this is the
        // panel's shape rather than a generic block.
        <SkeletonRows rows={4} height={14} gap={12} widths={["78%", "72%", "84%", "66%"]} />
      ) : bands.length === 0 ? (
        <p className="text-[14px]/[1.5] text-ink-3">
          難易度のついた復習がまだありません — FSRS
          は最初にカードをスケジュールしたときに難易度を割り当てます。
        </p>
      ) : (
        <>
          {bands.map((band) => (
            <div
              key={band.label}
              className="flex items-center gap-[9px] border-t border-line-soft pt-[5px]"
            >
              <span className="w-[52px] shrink-0 font-mono text-[14px]/[1.4] text-ink-2">
                {band.label}
              </span>
              <span
                className="chart-mark h-[10px] min-w-0 flex-1 overflow-hidden rounded-[var(--r-2)]"
                style={{ background: "var(--line-soft)" }}
                onMouseEnter={(event) =>
                  onEnter(event, {
                    title: `${band.label} の難易度`,
                    rows: [
                      { label: "復習数", value: band.reviews.toLocaleString() },
                      { label: "合格", value: band.passed.toLocaleString() },
                      { label: "正答率", value: pct(band.rate) },
                    ],
                  })
                }
                onMouseLeave={onLeave}
              >
                {/* Mapped over 50–100%, the range these bands actually occupy. Against 0–100 a
                    twenty-point spread is a fifth of the track and reads as no spread at all —
                    `Calibration`'s argument, and the design's own `(v - 65) / 32` scaling. */}
                <span
                  className="block h-full"
                  style={{
                    width: `${clamp((((band.rate ?? 0) * 100 - 50) / 50) * 100, 0, 100)}%`,
                    // `--trend-mature` is itself a red, so the old rule painted every band the
                    // same colour and said nothing. Blue above the line, danger below it.
                    background:
                      (band.rate ?? 1) < target ? "var(--accent-text)" : "var(--trend-young)",
                  }}
                />
              </span>
              <span className="w-[46px] shrink-0 text-right font-mono text-[14px]/[1.4] font-semibold text-ink">
                {pct(band.rate)}
              </span>
            </div>
          ))}
          <div className="flex items-baseline justify-between gap-[8px] border-t border-line-soft pt-[6px]">
            <span className="text-[14px]/[19.6px] text-ink-2">帯ごとのばらつき</span>
            <span className="font-mono text-[14px]/[19.6px] font-semibold text-ink">
              {spread === null ? "—" : `${(spread * 100).toFixed(1)}pt`}
            </span>
          </div>
          {/* The approximation, on screen. Difficulty is the card's value NOW applied to its whole
              history — `revlog` has no difficulty column, and the only per-review memory state Anki
              exposes is one backend call per card, which is what makes calibration a job. */}
          <span className="text-[14px]/[1.4] text-ink-4">
            各カードの現在の難易度で分類し、その全履歴に適用しています。
          </span>
        </>
      )}
      {card}
    </Panel>
  );
}


/**
 * Recall before its first payload: every panel, in its designed place, with its own shape.
 *
 * The titles are constants and so they render immediately — which is the point. A reader looking at
 * `TRUE RETENTION` above six grey rows knows what is arriving and where; a reader looking at
 * `Loading…` knows only that something is.
 *
 * It mirrors the two arrangements rather than stacking, because a skeleton that reflows into a
 * different layout when the data lands is a worse first impression than no skeleton at all.
 */
function RecallSkeleton({ wide }: { wide: boolean }) {
  const retention = (
    <Panel {...PANELS.trueRetention}>
      <SkeletonRows rows={6} height={18} gap={20} />
    </Panel>
  );
  const today = (
    <Panel {...PANELS.todaySoFar} gap={10}>
      <StatSkeleton note={UNITS.todayReviewed} width={118} />
      <SkeletonRows rows={3} height={12} gap={14} />
    </Panel>
  );
  const predicted = (
    <Panel {...PANELS.predictedRecall} note="FSRS" gap={10}>
      <div className="flex flex-wrap gap-[26px]">
        <StatSkeleton note={UNITS.predictedToday} />
        <StatSkeleton note={UNITS.predictedWeek} />
      </div>
      <SkeletonRows rows={2} height={12} gap={14} />
    </Panel>
  );
  const lapses = (
    <Panel {...PANELS.lapses} gap={10}>
      <SkeletonRows rows={6} height={12} gap={14} />
    </Panel>
  );
  const trend = (
    <Panel {...PANELS.trend} note={trendNote(TREND_WINDOW)} gap={8}>
      <SkeletonChart height={wide ? 240 : 130} />
    </Panel>
  );
  // Calibration owns its own request and so its own skeleton lives in `Calibration.tsx`; only the
  // heading is shared, and it is shared rather than copied.
  const calibration = (
    <Panel {...CALIBRATION_PANEL} gap={8}>
      <SkeletonRows rows={10} height={13} gap={16} />
    </Panel>
  );
  const dist = (panel: (typeof PANELS)["difficulty" | "stability"]) => (
    <Panel {...panel} gap={6}>
      <SkeletonChart height={wide ? 150 : 70} />
    </Panel>
  );
  const bands = (
    <Panel {...PANELS.passRate} gap={7}>
      <SkeletonRows rows={4} height={14} gap={12} widths={["78%", "72%", "84%", "66%"]} />
    </Panel>
  );
  const buttons = (
    <Panel {...PANELS.answerButtons} gap={9}>
      <SkeletonRows rows={4} height={22} gap={16} />
    </Panel>
  );
  const hours = (
    <Panel {...PANELS.reviewsByHour} gap={6}>
      <SkeletonChart height={wide ? 150 : 78} />
    </Panel>
  );

  if (wide) {
    return (
      <div className="flex flex-col gap-[14px]">
        <Band items={[[759, retention], [336, today], [366, predicted], [306, lapses]]} />
        <Band items={[[1024, trend], [769, calibration]]} />
        <Band
          items={[
            [298, dist(PANELS.difficulty)],
            [325, bands],
            [377, dist(PANELS.stability)],
            [430, hours],
            [325, buttons],
          ]}
        />
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-[14px]">
      <Columns wide={[retention]} narrow={[today, predicted]} />
      {trend}
      <Columns
        wide={[calibration, dist(PANELS.stability), hours]}
        narrow={[dist(PANELS.difficulty), bands, buttons, lapses]}
      />
    </div>
  );
}
